import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, RefreshCw, Radio, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";

const EMPTY = { title: "", description: "", presenter_name: "", banner_image: "", start_datetime: "", duration_minutes: 60 };

export default function AdminWebinarForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editing) {
      api.get(`/admin/webinars/${id}`).then((r) => {
        const w = r.data;
        setForm({
          title: w.title, description: w.description, presenter_name: w.presenter_name,
          banner_image: w.banner_image, start_datetime: (w.start_datetime || "").slice(0, 16),
          duration_minutes: w.duration_minutes,
        });
        setStream(w);
      });
    }
  }, [id, editing]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form, duration_minutes: Number(form.duration_minutes), start_datetime: new Date(form.start_datetime).toISOString() };
    try {
      if (editing) {
        const { data } = await api.put(`/admin/webinars/${id}`, payload);
        setStream(data);
        toast.success("Webinar updated");
      } else {
        const { data } = await api.post("/admin/webinars", payload);
        setStream(data);
        toast.success("Webinar created & stream provisioned");
        nav(`/admin/webinars/${data.id}/edit`, { replace: true });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };

  const regen = async () => {
    if (!window.confirm("Regenerating invalidates the current stream key and RTMP URL immediately. Continue?")) return;
    try {
      const { data } = await api.post(`/admin/webinars/${id}/regenerate-key`);
      const { data: fresh } = await api.get(`/admin/webinars/${id}`);
      setStream(fresh);
      toast.success("Stream key regenerated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const CopyRow = ({ label, value, secret }) => (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <div className="flex gap-2">
        <input readOnly value={value || ""} type={secret ? "text" : "text"} data-testid={`stream-${label}`}
          className="flex-1 bg-[#FFFFFF] border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm font-mono2 text-gray-600 outline-none" />
        <button type="button" onClick={() => copy(value)} className="border border-[#E4E4E7] hover:border-[#0090A8] hover:text-[#0090A8] rounded-sm px-3 transition-colors"><Copy size={16} /></button>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="p-8 max-w-4xl">
        <button onClick={() => nav("/admin/webinars")} className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#0A0A0A] mb-5 transition-colors">
          <ArrowLeft size={16} /> Back to webinars
        </button>
        <div className="overline mb-2">{editing ? "Edit" : "New"}</div>
        <h1 className="font-display font-black text-4xl tracking-tight mb-8">{editing ? "Edit Webinar" : "Schedule Webinar"}</h1>

        <div className="grid lg:grid-cols-2 gap-6">
          <form onSubmit={submit} className="bg-[#F5F5F5] border border-[#E4E4E7] rounded-sm p-6 space-y-4" data-testid="webinar-form">
            <div>
              <label className="overline block mb-1.5">Title *</label>
              <input data-testid="webinar-title" value={form.title} onChange={set("title")} required
                className="w-full bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm px-3 py-2 outline-none transition-colors" />
            </div>
            <div>
              <label className="overline block mb-1.5">Description</label>
              <textarea data-testid="webinar-description" value={form.description} onChange={set("description")} rows={3}
                className="w-full bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm px-3 py-2 outline-none transition-colors resize-none" />
            </div>
            <div>
              <label className="overline block mb-1.5">Presenter</label>
              <input data-testid="webinar-presenter" value={form.presenter_name} onChange={set("presenter_name")}
                className="w-full bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm px-3 py-2 outline-none transition-colors" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-1.5">Start (IST) *</label>
                <input data-testid="webinar-start" type="datetime-local" value={form.start_datetime} onChange={set("start_datetime")} required
                  className="w-full bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm px-3 py-2 outline-none transition-colors" />
              </div>
              <div>
                <label className="overline block mb-1.5">Duration (min) *</label>
                <input data-testid="webinar-duration" type="number" min={1} value={form.duration_minutes} onChange={set("duration_minutes")} required
                  className="w-full bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm px-3 py-2 outline-none transition-colors" />
              </div>
            </div>
            <div>
              <label className="overline block mb-1.5">Banner Image URL</label>
              <input data-testid="webinar-banner" value={form.banner_image} onChange={set("banner_image")} placeholder="https://…"
                className="w-full bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm px-3 py-2 outline-none transition-colors" />
            </div>
            <button data-testid="save-webinar" disabled={loading}
              className="w-full bg-[#0090A8] text-black hover:bg-[#00728A] font-bold rounded-sm py-2.5 transition-colors disabled:opacity-50">
              {loading ? "Saving…" : editing ? "Save Changes" : "Create & Provision Stream"}
            </button>
          </form>

          <div className="bg-[#F5F5F5] border border-[#E4E4E7] rounded-sm p-6" data-testid="stream-settings">
            <div className="flex items-center gap-2 mb-1">
              <Radio className="text-[#0090A8]" size={18} />
              <h2 className="font-display font-bold text-lg">Live Stream Settings</h2>
            </div>
            <p className="text-xs text-gray-500 mb-5">Paste these into Zoho Webinar → "Add a custom stream destination".</p>

            {stream ? (
              <div className="space-y-4">
                {stream.provider === "simulated" && (
                  <div className="flex gap-2 items-start bg-[#D92D20]/5 border border-[#D92D20]/20 rounded-sm p-3 text-xs text-gray-400">
                    <ShieldAlert size={16} className="text-[#D92D20] shrink-0 mt-0.5" />
                    <span><b className="text-[#D92D20]">SIMULATED</b> stream (Cloudflare Stream credentials not configured). The player shows a demo HLS feed. Add Cloudflare Stream credentials to go fully live.</span>
                  </div>
                )}
                <CopyRow label="Streaming Server URL (RTMP)" value={stream.rtmp_ingest_url} />
                <div>
                  <label className="overline block mb-1.5">Stream Key</label>
                  <div className="flex gap-2">
                    <input readOnly value={stream.stream_key || ""} data-testid="stream-key"
                      className="flex-1 bg-[#FFFFFF] border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm font-mono2 text-gray-600 outline-none" />
                    <button type="button" onClick={() => copy(stream.stream_key)} className="border border-[#E4E4E7] hover:border-[#0090A8] hover:text-[#0090A8] rounded-sm px-3 transition-colors"><Copy size={16} /></button>
                    <button type="button" onClick={regen} data-testid="regenerate-key" title="Regenerate" className="border border-[#E4E4E7] hover:border-[#D92D20] hover:text-[#D92D20] rounded-sm px-3 transition-colors"><RefreshCw size={16} /></button>
                  </div>
                </div>
                <CopyRow label="Event Page URL" value={stream.event_page_url} />
              </div>
            ) : (
              <div className="text-center text-gray-600 py-10 text-sm">
                Stream settings are generated automatically once you create the webinar.
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
